/**
 * Sticky Sub-Navigation Border Manager
 *
 * Manages the visibility of a bottom border on sticky sub-navigation menus
 * based on scroll position. Uses IntersectionObserver for optimal performance,
 * firing only when the scroll state changes rather than continuously during scroll.
 *
 * Behavior:
 * - Border hidden when page is at the top (maintains visual cohesion with hero section)
 * - Border visible when nav reaches its "stuck" position below the header
 *
 * @example
 * // In Blade template, add the sticky class:
 * <nav class="js-subNavigationMenu--sticky border-b border-transparent [&.is-scrolled]:fg-border-neutral-subtle">
 *
 * @see {@link ../utils/cssVariables.js} for CSS variable utilities
 * @see {@link ../modules/layoutVariables.js} for header offset management
 */

import {getCssVariableInPixels, LAYOUT_VARS} from '../utils/cssVariables.js';

/** @type {string} Selector for sticky sub-navigation elements */
const SELECTOR = '.js-subNavigationMenu--sticky';

/** @type {string} Class added when page has scrolled past the top */
const SCROLLED_CLASS = 'is-scrolled';

/**
 * Creates an IntersectionObserver to detect when the nav reaches its stuck position.
 *
 * @param {Element} nav - The navigation element to observe
 * @param {number} headerOffset - The header height in pixels (where nav sticks)
 * @returns {IntersectionObserver} The configured observer instance
 */
function createScrollObserver(nav, headerOffset) {
    return new IntersectionObserver(
        ([entry]) => {
            nav.classList.toggle(SCROLLED_CLASS, !entry.isIntersecting);
        },
        {
            // Negative top margin pushes the detection boundary down by header height
            // When nav crosses this boundary, it's considered "stuck"
            rootMargin: `-${headerOffset + 1}px 0px 0px 0px`,
            threshold: 1,
        }
    );
}

/**
 * Initializes the sticky border behavior for all matching sub-navigation elements.
 * Each nav element gets its own IntersectionObserver to detect when it reaches
 * its stuck position below the header.
 *
 * @returns {void}
 */
function initStickyBorder() {
    const subNavs = document.querySelectorAll(SELECTOR);

    subNavs.forEach((nav) => {
        const headerOffset = getCssVariableInPixels(LAYOUT_VARS.headerOffset);
        const observer = createScrollObserver(nav, headerOffset);
        observer.observe(nav);
    });
}

document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initStickyBorder)
    : initStickyBorder();
